import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import { LabelTypeEnum } from '../../../enums/label-enum';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import ArrowDownIcon from '../../Icons/ArrowDownIcon';
import Label from '../../MUIComponents/Label';
import BorderLinearProgress from './BorderLinearProgress';
import { useGetMyGamificationQuery } from '../../../store/apiSlice/gamification/gamificationApi';
import {
  currentLevelPercentage,
  gamificationScoreHelper,
  getNewLevelScoreThreshold,
  pointsToNextLevel,
} from '../../../utils/helper/gamification-score-helper';

const ProfileSidebarProgress = () => {
  const YOUR_PROFILE_SCORE = 'Your profile score';

  const [isScoreExpanded, setIsScoreExpanded] = useState(false);

  const { theme } = useThemeContext();
  const { data: gamification, isLoading: isGamificationLoading } =
    useGetMyGamificationQuery();

  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));

  const toggleExpand = () => {
    setIsScoreExpanded((prevState) => !prevState);
  };

  const ProfileScore = (
    <Box
      display="flex"
      flexDirection="column"
      gap="36px"
      sx={{
        p: { xs: '22px 16px', lg: '0' },
      }}
    >
      <Typography component="h3" variant="h3">
        {YOUR_PROFILE_SCORE}
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        bgcolor="secondary2.main"
        gap="22px"
        p={2}
      >
        <Box display="flex" flexDirection="column" gap="12px">
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body1">
              {gamificationScoreHelper(
                gamification?.profileScore?.totalCount ?? 0
              )}{' '}
            </Typography>
            <Box display="flex" flexDirection="row" gap={'0.1rem'}>
              <Typography variant="body1">
                {gamification?.profileScore?.totalCount ?? 0}
              </Typography>{' '}
              /{' '}
              <Typography variant="body1" color="text.secondary">
                {' '}
                {getNewLevelScoreThreshold(
                  gamification?.profileScore?.totalCount ?? 0
                )}
              </Typography>
            </Box>
          </Stack>
          <BorderLinearProgress
            variant="determinate"
            value={currentLevelPercentage(
              gamification?.profileScore?.totalCount ?? 0
            )}
          />
          <Box display="flex">
            <Typography
              width={'100%'}
              display="flex"
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              justifyContent={'center'}
            >
              {' '}
              {pointsToNextLevel(
                gamification?.profileScore?.totalCount ?? 0
              )}{' '}
              pts to next level
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Learn more about the benefits <br /> at the next level.
          </Typography>
        </Box>
      </Box>

      <Typography variant="body3" fontWeight={700} color={'text.primary'}>
        Next things to do:
      </Typography>
      <List sx={{ p: 0 }}>
        {gamification?.profileScoreCategories &&
          gamification?.profileScoreCategories?.length > 0 &&
          gamification?.profileScoreCategories.map(
            ({ id, title, points, disposable }, index) => (
              <Box key={id}>
                <ListItem
                  sx={{
                    p: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                  }}
                >
                  <Typography
                    typography={'body4'}
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: mobileView ? 'auto' : '10.9rem',
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    typography={'body4'}
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {points} points {disposable ? '' : 'each'}
                  </Typography>
                </ListItem>
                <Divider
                  sx={{
                    m:
                      index === gamification?.profileScoreCategories?.length - 1
                        ? '22px 0 0'
                        : '22px 0',
                  }}
                />
              </Box>
            )
          )}
      </List>
    </Box>
  );

  const ScoreOrLoader = isGamificationLoading
    ? skeletonProfileSidebarProgress
    : ProfileScore;

  return (
    <Box
      display="flex"
      flexDirection="column"
      bgcolor={'background.white'}
      sx={{
        borderRadius: '1px',
        p: { xs: '0', lg: '36px 22px' },
      }}
      onClick={toggleExpand}
    >
      {mobileView && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'background.light',
            p: '16px',
          }}
        >
          <Typography variant="badge2" textTransform={'uppercase'}>
            {YOUR_PROFILE_SCORE}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Label
              labelType={LabelTypeEnum.VERIFIED}
              sx={{
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.5px',
                lineHeight: '12px',
              }}
            >
              {gamificationScoreHelper(
                gamification?.profileScore?.totalCount ?? 0
              )}
            </Label>
            <ArrowDownIcon
              sx={{
                width: '28px',
                height: '28px',
                transform: isScoreExpanded ? 'rotate(180deg)' : '',
              }}
            />
          </Box>
        </Box>
      )}
      {mobileView ? (
        <Collapse in={isScoreExpanded}>{ScoreOrLoader}</Collapse>
      ) : (
        ScoreOrLoader
      )}
      <Box
        sx={{
          display: mobileView ? 'block' : 'none',
          backgroundColor: 'background.default',
          height: '16px',
        }}
      />
    </Box>
  );
};

export default ProfileSidebarProgress;

const skeletonProfileSidebarProgress = (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '36px',
      p: { xs: '22px 16px', lg: '0' },
    }}
  >
    <Skeleton
      variant="rectangular"
      sx={{
        width: '100%',
        height: '2rem',
      }}
    />
    <Skeleton
      variant="rectangular"
      sx={{
        width: '100%',
        height: '25rem',
      }}
    />
  </Box>
);
